import { useTranslation } from '@multaqa/i18n'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

/**
 * What posted a journal entry, and how to get back to it.
 *
 * The posting engine stamps `source_type`/`source_id` on every entry it
 * writes, so the ledger already knows a payroll run, an invoice, a payment, a
 * fixed asset or an approved expense claim caused it. Nothing rendered that,
 * which left the strongest thing this system does — one confirmation moving
 * HR, Payroll and Accounting together — true in the database and invisible on
 * screen.
 *
 * Two spellings of the same source exist in the data and both must resolve.
 * Accounting's own actions pass a slug (`invoice`, `payroll_run`), while HR
 * passes the model's fully-qualified class name through `LedgerContract::
 * record()`. Normalising the backend would desync the `ledger_posting_intents`
 * rows already written for the replay path, so the mapping is done here and
 * the FQCN is matched on its trailing segment.
 */

/** A source we can name and, where a screen exists, link to. */
type JournalSourceLink =
  | { to: '/accounting/invoices/$invoiceId'; params: { invoiceId: string } }
  | { to: '/accounting/bills/$billId'; params: { billId: string } }
  | { to: '/accounting/payments/$paymentId'; params: { paymentId: string } }
  | { to: '/accounting/deposits/$depositId'; params: { depositId: string } }
  | { to: '/payroll/runs/$runId'; params: { runId: string } }
  | { to: '/hr/expenses/$claimId'; params: { claimId: string } }
  | { to: '/accounting/expenses/$documentId'; params: { documentId: string } }
  | { to: '/accounting/cash-sales/$cashSaleId'; params: { cashSaleId: string } }
  | { to: '/hr/cash-custody/$custodyId'; params: { custodyId: string } }
  | { to: '/accounting/assets/$assetId'; params: { assetId: string } }
  | { to: '/accounting/opening-balances/$batchId'; params: { batchId: string } }
  | { to: '/sales/orders/$orderId'; params: { orderId: string } }
  | { to: '/accounting/journal/$entryId'; params: { entryId: string } }

interface SourceTarget {
  /** Translation key under `accounting.entrySource.*`. */
  labelKey: string
  /** Built lazily so a source with no detail screen can omit it. */
  href?: (sourceId: string) => JournalSourceLink
}

const SOURCES: Record<string, SourceTarget> = {
  // A manual entry has no source; the register and the statements name it `journal`.
  journal: { labelKey: 'manual' },
  // Not a posting source: a related-documents list links to an entry itself.
  journal_entry: {
    labelKey: 'journalEntry',
    href: (id) => ({ to: '/accounting/journal/$entryId', params: { entryId: id } }),
  },
  invoice: {
    labelKey: 'invoice',
    href: (id) => ({ to: '/accounting/invoices/$invoiceId', params: { invoiceId: id } }),
  },
  invoice_cost: {
    labelKey: 'invoiceCost',
    href: (id) => ({ to: '/accounting/invoices/$invoiceId', params: { invoiceId: id } }),
  },
  bill: {
    labelKey: 'bill',
    href: (id) => ({ to: '/accounting/bills/$billId', params: { billId: id } }),
  },
  payment: {
    labelKey: 'payment',
    href: (id) => ({ to: '/accounting/payments/$paymentId', params: { paymentId: id } }),
  },
  bank_deposit: {
    labelKey: 'bankDeposit',
    href: (id) => ({ to: '/accounting/deposits/$depositId', params: { depositId: id } }),
  },
  bank_transfer: { labelKey: 'bankTransfer' },
  deposit: { labelKey: 'bankDeposit' },
  transfer: { labelKey: 'bankTransfer' },
  payroll_run: {
    labelKey: 'payrollRun',
    href: (id) => ({ to: '/payroll/runs/$runId', params: { runId: id } }),
  },
  expense_claim: {
    labelKey: 'expenseClaim',
    href: (id) => ({ to: '/hr/expenses/$claimId', params: { claimId: id } }),
  },
  fixed_asset: {
    labelKey: 'fixedAsset',
    href: (id) => ({ to: '/accounting/assets/$assetId', params: { assetId: id } }),
  },
  fixed_asset_depreciation: {
    labelKey: 'depreciation',
  },
  fixed_asset_disposal: {
    labelKey: 'fixedAssetDisposal',
    href: (id) => ({ to: '/accounting/assets/$assetId', params: { assetId: id } }),
  },
  opening_balance: {
    labelKey: 'openingBalance',
  },
  revaluation: {
    labelKey: 'revaluation',
  },
  money_document: {
    labelKey: 'moneyDocument',
    href: (id) => ({ to: '/accounting/expenses/$documentId', params: { documentId: id } }),
  },
  expense: {
    labelKey: 'expense',
    href: (id) => ({ to: '/accounting/expenses/$documentId', params: { documentId: id } }),
  },
  cheque: {
    labelKey: 'cheque',
    href: (id) => ({ to: '/accounting/expenses/$documentId', params: { documentId: id } }),
  },
  card_credit: {
    labelKey: 'cardCredit',
    href: (id) => ({ to: '/accounting/expenses/$documentId', params: { documentId: id } }),
  },
  cash_sale: {
    labelKey: 'cashSale',
    href: (id) => ({ to: '/accounting/cash-sales/$cashSaleId', params: { cashSaleId: id } }),
  },
  cash_sale_cost: {
    labelKey: 'cashSaleCost',
    href: (id) => ({ to: '/accounting/cash-sales/$cashSaleId', params: { cashSaleId: id } }),
  },
  cash_sale_void_cost: {
    labelKey: 'cashSaleVoidCost',
    href: (id) => ({ to: '/accounting/cash-sales/$cashSaleId', params: { cashSaleId: id } }),
  },
  cash_custody: {
    labelKey: 'cashCustody',
    href: (id) => ({ to: '/hr/cash-custody/$custodyId', params: { custodyId: id } }),
  },
  prepaid_schedule_entry: { labelKey: 'prepaidScheduleEntry' },
  opening_balance_batch: {
    labelKey: 'openingBalanceBatch',
    href: (id) => ({ to: '/accounting/opening-balances/$batchId', params: { batchId: id } }),
  },
  sales_order: {
    labelKey: 'salesOrder',
    href: (id) => ({ to: '/sales/orders/$orderId', params: { orderId: id } }),
  },
  customer_return: { labelKey: 'customerReturn' },
  supplier_return: { labelKey: 'supplierReturn' },
  stock_movement: { labelKey: 'stockMovement' },
  stock_count_session: { labelKey: 'stockCountSession' },
  variant_cost_adjustments: { labelKey: 'variantCostAdjustments' },
  sales_order_revenue_correction: { labelKey: 'salesOrderRevenueCorrection' },
  employee_salary_advance: { labelKey: 'employeeSalaryAdvance' },
}

/**
 * Reduce a raw `source_type` to a key in SOURCES.
 *
 * `App\Modules\HR\Models\ExpenseClaim` → `expense_claim`: take the last
 * backslash-separated segment and convert PascalCase to snake_case. A slug
 * passes through the same path unchanged.
 */
export function normaliseSourceType(sourceType: string): string {
  const leaf = sourceType.split('\\').pop() ?? sourceType
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase()
}

/**
 * The name a list shows for a source: its label where SOURCES knows it, the
 * other-module label for one it does not, and the manual-entry label when there
 * is no source. Registers and reports name a source through this, never by its
 * raw value.
 */
export function journalSourceLabel(
  t: (key: string) => string,
  sourceType: string | null | undefined,
): string {
  if (!sourceType) return t('entrySource.manual')
  const target = SOURCES[normaliseSourceType(sourceType)]
  return target ? t(`entrySource.${target.labelKey}`) : t('entrySource.other')
}

export function journalSourceLink(
  sourceType: string | null | undefined,
  sourceId: string | null | undefined,
): JournalSourceLink | null {
  if (!sourceType || !sourceId) return null
  const target = SOURCES[normaliseSourceType(sourceType)]
  return target?.href?.(sourceId) ?? null
}

export interface JournalEntrySourceProps {
  sourceType: string | null | undefined
  sourceId: string | null | undefined
  sourceAction?: string | null
  /** Falls back to this when the entry has no source — a manual entry. */
  fallback?: string
}

/**
 * The source of an entry as a link where a detail screen exists, as plain
 * text where it does not, and as the manual-entry label when there is no
 * source at all.
 */
export function JournalEntrySource({
  sourceType,
  sourceId,
  sourceAction,
  fallback,
}: JournalEntrySourceProps) {
  const { t } = useTranslation('accounting')

  if (!sourceType) {
    return <span className="text-muted-foreground">{fallback ?? t('entrySource.manual')}</span>
  }

  const target = SOURCES[normaliseSourceType(sourceType)]

  // An unmapped source still gets named rather than hidden — a new module's
  // postings should read as "something posted this", never as a blank cell.
  if (!target) {
    return <span className="text-muted-foreground">{t('entrySource.other')}</span>
  }

  const label = t(`entrySource.${target.labelKey}`)

  const actionLabel = sourceAction ? t(`journalEntryDetail.sourceActions.${sourceAction}`) : label
  const link = journalSourceLink(sourceType, sourceId)

  if (!link) {
    return (
      <span className="inline-flex flex-wrap gap-x-1">
        <span>{label}</span>
        {sourceAction ? <span>{actionLabel}</span> : null}
      </span>
    )
  }

  return (
    <SourceDocumentLink sourceType={sourceType} sourceId={sourceId}>
      {actionLabel}
    </SourceDocumentLink>
  )
}

/**
 * Children wrapped in a link to the document that posted them, or the children
 * alone when there is no such screen. The one place a source link is styled, so
 * statements, registers and reports read the same.
 */
export function SourceDocumentLink({
  sourceType,
  sourceId,
  children,
}: {
  sourceType: string | null | undefined
  sourceId: string | null | undefined
  children: ReactNode
}) {
  const link = journalSourceLink(sourceType, sourceId)
  if (!link) return <>{children}</>

  return (
    <Link
      // The route table is generated, so `to` is a plain string here and the
      // typed-router generics cannot narrow it.
      to={link.to as never}
      params={link.params as never}
      className="hover:text-foreground underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      {children}
    </Link>
  )
}
